import Select from "react-select";
import type {
  SingleValue,
  Props as SelectProps,
  GroupBase,
} from "react-select";
import type { Option } from "Models";

type FieldLike<T> = {
  value: T;
  onChange: (value: T) => void;
  onBlur?: () => void;
  name?: string;
  ref?: React.Ref<any>;
};

// Base props shared by both variants
type BaseEnumSelectProps<T> = {
  options: Option<T>[];
  name?: string;
} & Omit<
  SelectProps<Option<T>, false, GroupBase<Option<T>>>,
  "options" | "value" | "onChange" | "onBlur" | "name" | "ref"
>;

// With field (react-hook-form)
type EnumSelectWithFieldProps<T> = BaseEnumSelectProps<T> & {
  field: FieldLike<T>;
  value?: never;
  onChange?: never;
  onValueChange?: (value: T) => void; // Side effects after field.onChange
};

// Standalone (no form)
type EnumSelectStandaloneProps<T> = BaseEnumSelectProps<T> & {
  field?: never;
  value: T | undefined;
  onChange: (value: T) => void;
  onValueChange?: never;
};

type EnumSelectProps<T> =
  | EnumSelectWithFieldProps<T>
  | EnumSelectStandaloneProps<T>;

export function EnumSelect<T>({
  options,
  field,
  value: valueProp,
  onChange: onChangeProp,
  onValueChange,
  name,
  ...rest
}: EnumSelectProps<T>) {
  // Determine value and onChange based on which pattern is used
  const value = field ? field.value : valueProp;

  const handleChange = (selected: SingleValue<Option<T>>) => {
    if (selected) {
      if (field) {
        field.onChange(selected.value);
        onValueChange?.(selected.value);
      } else if (onChangeProp) {
        onChangeProp(selected.value);
      }
    }
  };

  return (
    <Select<Option<T>>
      options={options}
      value={
        value !== undefined ? options.find((opt) => opt.value === value) : null
      }
      onChange={handleChange}
      onBlur={field?.onBlur}
      name={name ?? field?.name}
      ref={field?.ref}
      {...rest}
    />
  );
}
